import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import PLANE from "../../../assets/images/plane.png";
import PHONE from "../../../assets/images/phone.png";
import MAIL from "../../../assets/images/mail.png";
import { getBookingDetails } from "../services/mytrips.services";
import { Link, useLocation } from "react-router-dom";
import Loading from "../../../components/spinner/Loading";
import useWindowSize from "../../../_hooks/useWindowSize";
import { ErrorToast } from "../../../toast/Toasts";
import { API_URL } from "../../../app/config";
import { BsFillFileEarmarkPdfFill, BsFillFileTextFill } from "react-icons/bs";
import { HiDownload } from "react-icons/hi";
import { calculateFareDetailsFromReprint } from "../../../_helpers/fareUtils";
import { getRequestId } from "../../../_helpers/miscUtils";
import { getFlightImage } from "../../../_helpers/flightUtils";
import { formatIndianNumber } from "../../../_helpers/formatUtils";
import SendInvoiceDialog from "../dialogs/SendInvoiceDialog";
import SendTicketDialog from "../dialogs/SendTicketDialog";
import { FlightImage } from "../../../components/flightimage/FlightImage";

const TripConfirmation = () => {
  const dispatch = useAppDispatch();
  const { width } = useWindowSize();
  const loading = useAppSelector((state) => state?.mytrips?.loading);
  const location = useLocation();

  const [ticketLoading, setTicketLoading] = useState<any>(false);
  const [invoiceLoading, setInvoiceLoading] = useState<any>(false);
  const [ticketLoadingIndexes, setTicketLoadingIndexes] = useState<number[]>(
    []
  );
  const [invoiceLoadingIndexes, setInvoiceLoadingIndexes] = useState<number[]>(
    []
  );

  const BookingDetails = useAppSelector(
    (state) => state?.mytrips?.BookinDetails
  );

  const isMultiPnr = BookingDetails?.AirPNRDetails?.length > 1;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bookingId = params.get("bookingId");

    dispatch(
      getBookingDetails({
        Booking_RefNo: bookingId,
        Airline_PNR: "",
      })
    );
  }, []);

  const faredetails = calculateFareDetailsFromReprint(
    BookingDetails?.AirPNRDetails || []
  );

  const handleDownloadPdf = async (
    Booking_RefNo: string,
    Airline_PNR: string,
    index: number
  ) => {
    try {
      if (isMultiPnr) {
        setTicketLoadingIndexes((prev) => [...prev, index]);
      } else {
        setTicketLoading(true);
      }
      const Request_Id = getRequestId();

      const response = await fetch(`${API_URL}/AirlineReprint/Air_ReprintPDF`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Booking_RefNo,
          Airline_PNR,
          IsPayment: false,
          Request_Id,
        }),
      });

      if (!response.ok) {
        ErrorToast("Failed to generate PDF! - Please try after some time");
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Ticket-${Airline_PNR}.pdf`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      ErrorToast("Failed to download Ticket! - Please try again later");
    } finally {
      if (isMultiPnr) {
        setTicketLoadingIndexes((prev) => prev.filter((i) => i !== index));
      } else {
        setTicketLoading(false);
      }
    }
  };

  const handleDownloadInvoice = async (
    Booking_RefNo: string,
    Airline_PNR: string,
    index: number
  ) => {
    try {
      if (isMultiPnr) {
        setInvoiceLoadingIndexes((prev) => [...prev, index]);
      } else {
        setInvoiceLoading(true);
      }

      const Request_Id = getRequestId();

      const response = await fetch(
        `${API_URL}/AirlineReprint/Air_ReprintInvoice`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Booking_RefNo,
            Airline_PNR,
            IsPayment: false,
            Request_Id,
          }),
        }
      );

      if (!response.ok) {
        ErrorToast("Failed to generate PDF! - Please try after some time");
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice-${Airline_PNR}.pdf`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      ErrorToast("Failed to download Invoice! - Please try again later");
    } finally {
      if (isMultiPnr) {
        setInvoiceLoadingIndexes((prev) => prev.filter((i) => i !== index));
      } else {
        setInvoiceLoading(false);
      }
    }
  };

  return (
    <>
      <Loading loading={loading} backgroundColor="white" />
      {!loading && (
        <div className="Booking-page">
          <div className="container">
            <div className="booking-confirmationbar">
              <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-12">
                  <svg
                    width={55}
                    height={55}
                    viewBox="0 0 55 55"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20.625 27.4999L25.2083 32.0832L34.375 22.9166M50.4167 27.4999C50.4167 40.1564 40.1565 50.4166 27.5 50.4166C14.8435 50.4166 4.58334 40.1564 4.58334 27.4999C4.58334 14.8434 14.8435 4.58325 27.5 4.58325C40.1565 4.58325 50.4167 14.8434 50.4167 27.4999Z"
                      stroke="#08A34E"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <h2>Your Booking is Confirmed!</h2>
                  <p>
                    Thank you for booking with us. You will soon receive a
                    confirmation via email.
                  </p>
                  <div className="flight-indcator-id">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5>
                        Booking Ref No:{" "}
                        <strong>{BookingDetails?.portal_booking_no}</strong>
                      </h5>
                      <h5>
                        Airline PNR:{" "}
                        <strong>
                          {BookingDetails?.AirPNRDetails?.length
                            ? BookingDetails.AirPNRDetails.map(
                                (e: any) => e?.Airline_PNR
                              ).join(", ")
                            : ""}
                        </strong>
                      </h5>
                    </div>
                    <div className="d-flex justify-content-between">
                      <p>
                        <strong>Booked on: </strong>
                        {BookingDetails &&
                          moment(
                            BookingDetails?.Booking_DateTime,
                            "DD/MM/YYYY HH:mm:ss"
                          ).format("DD/MM/YYYY | hh:mm:ss A")}
                      </p>
                      <ul className="ticket-list d-flex gap-3">
                        <li
                          className="position-relative"
                          onClick={
                            !isMultiPnr
                              ? () =>
                                  !ticketLoading &&
                                  handleDownloadPdf(
                                    BookingDetails?.portal_booking_no,
                                    BookingDetails?.AirPNRDetails &&
                                      BookingDetails?.AirPNRDetails[0]
                                        ?.Airline_PNR,
                                    0
                                  )
                              : undefined
                          }
                          {...(isMultiPnr && {
                            "data-bs-toggle": "modal",
                            "data-bs-target": "#downloadticket_popup",
                          })}
                        >
                          <span className="icon">
                            {ticketLoading ? (
                              <div
                                className="spinner-border text-primary me-1"
                                role="status"
                                style={{ width: 15, height: 15 }}
                              >
                                <span className="sr-only"></span>
                              </div>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="20"
                                viewBox="0 0 24 24"
                                fill={"#007BFF"}
                              >
                                <path d="M0 0h24v24H0V0z" fill="none" />
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18h14v2H5z" />
                              </svg>
                            )}
                          </span>
                          <span
                            style={{
                              color: ticketLoading ? "#999999" : "#007BFF",
                            }}
                          >
                            E-Ticket(s)
                          </span>
                        </li>
                        <li
                          onClick={
                            !isMultiPnr
                              ? () =>
                                  !invoiceLoading &&
                                  handleDownloadInvoice(
                                    BookingDetails?.portal_booking_no,
                                    BookingDetails?.AirPNRDetails &&
                                      BookingDetails?.AirPNRDetails[0]
                                        ?.Airline_PNR,
                                    0
                                  )
                              : undefined
                          }
                          {...(isMultiPnr && {
                            "data-bs-toggle": "modal",
                            "data-bs-target": "#downloadinvoice_popup",
                          })}
                        >
                          <span className="icon">
                            {invoiceLoading ? (
                              <div
                                className="spinner-border text-primary me-1"
                                role="status"
                                style={{ width: 15, height: 15 }}
                              >
                                <span className="sr-only"></span>
                              </div>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="20"
                                viewBox="0 0 24 24"
                                fill={"#007BFF"}
                              >
                                <path d="M0 0h24v24H0V0z" fill="none" />
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18h14v2H5z" />
                              </svg>
                            )}
                          </span>
                          <span
                            style={{
                              color: invoiceLoading ? "#999999" : "#007BFF",
                            }}
                          >
                            Invoice(s)
                          </span>
                        </li>
                        <li
                          data-bs-toggle="modal"
                          data-bs-target="#sendticket-form"
                        >
                          <span className="icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="#007bff"
                            >
                              <path d="M0 0h24v24H0z" fill="none" />
                              <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
                            </svg>
                          </span>
                          <span>Ticket</span>
                        </li>
                        <li
                          data-bs-toggle="modal"
                          data-bs-target="#sendinvoice-form"
                        >
                          <span className="icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="#007bff"
                            >
                              <path d="M0 0h24v24H0z" fill="none" />
                              <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
                            </svg>
                          </span>
                          <span>Invoice</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-8 col-md-7 col-sm-12">
                <div className="booking-page-right travel-detail-new">
                  <h3>Flight Details</h3>
                  <div className="fligt-confirm">
                    <div className="booking-timing">
                      <div className="booking-flight-half">
                        {BookingDetails?.AirPNRDetails?.map(
                          (tripInfo: any, tripIndex: number) => (
                            <>
                              {tripInfo?.Flights?.map(
                                (flight: any, flightIndex: number) => {
                                  const segments = flight?.Segments || [];

                                  return (
                                    <div
                                      key={`${tripIndex}-${flightIndex}`}
                                      className={`${
                                        tripIndex + 1 <
                                          BookingDetails?.AirPNRDetails
                                            ?.length ||
                                        flightIndex + 1 <
                                          tripInfo?.Flights?.length
                                          ? "flight-review-card"
                                          : ""
                                      }`}
                                    >
                                      <div key={flightIndex}>
                                        <h4>
                                          {segments[0]?.Origin} →{" "}
                                          {
                                            segments[segments.length - 1]
                                              ?.Destination
                                          }
                                        </h4>

                                        {segments.map(
                                          (item: any, index: number) => (
                                            <div
                                              className="booking-timimg-main"
                                              key={index}
                                            >
                                              <h5>Flight {index + 1}</h5>
                                              <div className="route-timing border-0 p-0">
                                                <div className="flight-choose">
                                                  <div className="fight-name">
                                                    <FlightImage
                                                      key={item?.Airline_Code}
                                                      flightCode={
                                                        item?.Airline_Code
                                                      }
                                                    />
                                                    <span>
                                                      <h5>
                                                        {item?.Airline_Name}
                                                      </h5>
                                                    </span>
                                                  </div>
                                                </div>

                                                <div className="route-strating">
                                                  {moment(
                                                    item?.Departure_DateTime
                                                  ).format("HH:mm")}
                                                  <p>
                                                    <strong>
                                                      {item?.Origin}
                                                    </strong>
                                                    {item?.Origin_Terminal
                                                      ? `, Terminal : ${item?.Origin_Terminal}`
                                                      : ""}
                                                  </p>
                                                </div>
                                                {/* TODO need to change the non stop to actual */}
                                                <div className="cunsume-time">
                                                  <p>
                                                    Non Stop |{" "}
                                                    <strong>
                                                      {item?.Duration}
                                                    </strong>
                                                  </p>
                                                  <div className="cunsume-pic">
                                                    <img
                                                      src={PLANE}
                                                      alt="plane"
                                                    />
                                                  </div>
                                                  <p>
                                                    {moment(
                                                      item?.Departure_DateTime
                                                    ).format("dddd, DD MMM’YY")}
                                                  </p>
                                                </div>

                                                <div className="route-strating route-arrival">
                                                  {moment(
                                                    item?.Arrival_DateTime
                                                  ).format("HH:mm")}
                                                  <p>
                                                    <strong>
                                                      {item?.Destination}
                                                    </strong>
                                                    {item?.Destination_Terminal
                                                      ? `, Terminal : ${item?.Destination_Terminal}`
                                                      : ""}
                                                  </p>
                                                </div>
                                              </div>
                                              {/* TODO need to change the non stop to actual */}
                                              <div className="mobile-timimg">
                                                <div className="cunsume-time">
                                                  <p>
                                                    Non Stop |{" "}
                                                    <strong>
                                                      {item?.Duration}
                                                    </strong>
                                                  </p>
                                                  <p>
                                                    {moment(
                                                      item?.Departure_DateTime
                                                    ).format("dddd, DD MMM’YY")}
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            </>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="travel-contact">
                    <div className="traveller-list">
                      <h4>Traveller Details</h4>
                      {BookingDetails &&
                        BookingDetails?.AirPNRDetails &&
                        BookingDetails?.AirPNRDetails?.length > 0 &&
                        BookingDetails?.AirPNRDetails?.map(
                          (pnrDetails: any, pnrIndex: number) => (
                            <React.Fragment key={pnrDetails + "-" + pnrIndex}>
                              {pnrDetails?.PAXTicketDetails?.some(
                                (e: any) => e?.TicketStatus === "Live"
                              ) &&
                                pnrDetails?.Flights?.map(
                                  // TODO if its not working then need to change the logic
                                  (flight: any, flightIndex: number) => {
                                    const segments = flight?.Segments || [];
                                    const origin = segments[0]?.Origin;
                                    const destination =
                                      segments[segments.length - 1]
                                        ?.Destination;

                                    return (
                                      <h5 key={flightIndex}>
                                        {origin} → {destination} -{" "}
                                        {pnrDetails?.Airline_PNR}
                                      </h5>
                                    );
                                  }
                                )}
                              {pnrDetails?.PAXTicketDetails?.map(
                                (traveller: any, index: any) => {
                                  if (traveller?.TicketStatus === "Live") {
                                    return (
                                      <div
                                        className="sidebar-content"
                                        key={index}
                                      >
                                        <span>
                                          {`${index + 1}. ${traveller?.Title} ${
                                            traveller?.First_Name
                                          } ${traveller?.Last_Name}`}
                                        </span>
                                        <div className="sidebar-left" />
                                        <div className="sidebar-right">
                                          <span>
                                            {traveller?.Pax_type == 1
                                              ? "CHILD"
                                              : traveller?.Pax_type == 2
                                              ? "INFANT"
                                              : "ADULT"}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  }
                                }
                              )}
                            </React.Fragment>
                          )
                        )}
                    </div>
                    <div
                      className="traveller-contact-detail traveller-list"
                      style={{ height: "fit-content" }}
                    >
                      <h4>Contact Detail</h4>
                      {BookingDetails?.PAX_EmailId && (
                        <Link to={`mailto:${BookingDetails?.PAX_EmailId}`}>
                          <img src={MAIL} alt="e-mail" />
                          {BookingDetails?.PAX_EmailId}
                        </Link>
                      )}
                      {BookingDetails?.PAX_Mobile && (
                        <Link to={`tel:++91 ${BookingDetails?.PAX_Mobile}`}>
                          <img src={PHONE} alt="phone" />
                          {BookingDetails?.PAX_Mobile}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4 col-md-5 col-sm-12">
                <h3>Fare Details</h3>
                <div
                  className="listing-sidebar booking-sidebar border-0"
                  style={{ top: width <= 991 ? 0 : 100 }}
                >
                  <div className="sidebar-content ">
                    <div className="sidebar-left">
                      <h5>Base Fair</h5>
                    </div>
                    <div className="sidebar-right">
                      <h5>
                        {BookingDetails &&
                          `₹ ${formatIndianNumber(
                            (faredetails?.adults?.basic || 0) +
                              (faredetails?.children?.basic || 0) +
                              (faredetails?.infants?.basic || 0)
                          )}`}
                      </h5>
                    </div>
                  </div>
                  <div className="sidebar-content ">
                    <div className="sidebar-left">
                      <h5>Taxes & Fees</h5>
                    </div>
                    <div className="sidebar-right">
                      <h5>
                        {BookingDetails &&
                          `₹ ${formatIndianNumber(
                            (faredetails?.adults?.taxes || 0) +
                              (faredetails?.children?.taxes || 0) +
                              (faredetails?.infants?.taxes || 0)
                            // (faredetails?.overall?.serviceAmount || 0)
                          )}`}
                      </h5>
                    </div>
                  </div>
                  {BookingDetails?.convenience_fee > 0 && (
                    <div className="sidebar-content pb-1">
                      <div className="sidebar-left">
                        <h5>Convenience Fee</h5>
                      </div>
                      <div className="sidebar-right">
                        <h5>
                          {BookingDetails &&
                            `₹ ${formatIndianNumber(
                              BookingDetails?.convenience_fee
                            )}`}
                        </h5>
                      </div>
                    </div>
                  )}
                  <div
                    className="sidebar-content siderbar-total"
                    style={{
                      borderTop: "1px dashed rgb(0 0 0 / 20%)",
                      padding: "10px 0",
                    }}
                  >
                    <div className="sidebar-left">
                      <h5>
                        <strong>Grand Total</strong>
                      </h5>
                    </div>
                    <div className="sidebar-right">
                      <h5>
                        <strong>
                          {BookingDetails &&
                            `₹ ${formatIndianNumber(
                              (faredetails?.adults?.total || 0) +
                                (faredetails?.children?.total || 0) +
                                (faredetails?.infants?.total || 0) +
                                (BookingDetails?.convenience_fee || 0)
                            )}`}
                        </strong>
                      </h5>
                    </div>
                  </div>
                  {BookingDetails?.PromoCodeValue > 0 && (
                    <div className="sidebar-content pb-2">
                      <div className="sidebar-left">
                        <h5 style={{ color: "#00a34d" }}>
                          PromoCode ({BookingDetails?.PromoCode})
                        </h5>
                      </div>
                      <div className="sidebar-right">
                        <h5 style={{ color: "#00a34d" }}>
                          {BookingDetails &&
                            `- ₹ ${formatIndianNumber(
                              BookingDetails?.PromoCodeValue
                            )}`}
                        </h5>
                      </div>
                    </div>
                  )}
                  {BookingDetails?.GiftCodeValue > 0 && (
                    <div className="sidebar-content pb-2">
                      <div className="sidebar-left">
                        <h5 style={{ color: "#00a34d" }}>
                          GiftCode ({BookingDetails?.GiftCode})
                        </h5>
                      </div>
                      <div className="sidebar-right">
                        <h5 style={{ color: "#00a34d" }}>
                          {BookingDetails &&
                            `- ₹ ${formatIndianNumber(
                              BookingDetails?.GiftCodeValue
                            )}`}
                        </h5>
                      </div>
                    </div>
                  )}
                  {BookingDetails?.wallet_balance > 0 && (
                    <div className="sidebar-content pb-2">
                      <div className="sidebar-left">
                        <h5 style={{ color: "#00a34d" }}>Used Wallet Amount</h5>
                      </div>
                      <div className="sidebar-right">
                        <h5 style={{ color: "#00a34d" }}>
                          {BookingDetails &&
                            `- ₹ ${formatIndianNumber(
                              BookingDetails?.wallet_balance
                            )}`}
                        </h5>
                      </div>
                    </div>
                  )}
                  {BookingDetails?.CreditLimitUsedAmount > 0 && (
                    <div className="sidebar-content pb-0">
                      <div className="sidebar-left">
                        <h5 style={{ color: "#00a34d" }}>Used Credit Limit</h5>
                      </div>
                      <div className="sidebar-right">
                        <h5 style={{ color: "#00a34d" }}>
                          {BookingDetails &&
                            `- ₹ ${formatIndianNumber(
                              BookingDetails?.CreditLimitUsedAmount
                            )}`}
                        </h5>
                      </div>
                    </div>
                  )}
                  <div
                    className="sidebar-content siderbar-total"
                    style={{
                      borderTop: "1px dashed rgb(0 0 0 / 20%)",
                      padding: "10px 0",
                    }}
                  >
                    <div className="sidebar-left">
                      <h5>
                        <strong>Total Paid</strong>
                      </h5>
                    </div>
                    <div className="sidebar-right">
                      <h5>
                        <strong>
                          {BookingDetails &&
                            `₹ ${formatIndianNumber(
                              (faredetails?.adults?.total || 0) +
                                (faredetails?.children?.total || 0) +
                                (faredetails?.infants?.total || 0) +
                                (BookingDetails?.convenience_fee || 0) -
                                (BookingDetails?.PromoCodeValue || 0) -
                                (BookingDetails?.GiftCodeValue || 0) -
                                (BookingDetails?.wallet_balance || 0) -
                                (BookingDetails?.CreditLimitUsedAmount || 0)
                            )}`}
                        </strong>
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal fade"
            id={`downloadticket_popup`}
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex={-1}
            aria-labelledby="downloadticket-popup"
            aria-hidden="true"
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: 500 }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title fs-5" id="edit-profile">
                    Download E-Ticket(s)
                  </h4>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body mt-2">
                  {BookingDetails &&
                    BookingDetails?.AirPNRDetails?.map(
                      (tripInfo: any, tripIndex: any) => {
                        return (
                          <div className="invoice-card">
                            <div className="icon">
                              <BsFillFileEarmarkPdfFill />
                            </div>
                            <div className="info">
                              <div className="title">
                                {tripInfo?.Flights[0]?.Segments[0]?.Origin} →{" "}
                                {
                                  tripInfo?.Flights[0]?.Segments[
                                    tripInfo?.Flights[0]?.Segments?.length - 1
                                  ]?.Destination
                                }
                              </div>
                              <div className="date">
                                {moment(
                                  tripInfo?.Flights[0]?.Segments[0]
                                    ?.Departure_DateTime,
                                  "MM/DD/YYYY HH:mm:ss"
                                ).format("D MMMM")}
                                ,{" "}
                                {
                                  tripInfo?.Flights[0]?.Segments[0]
                                    ?.Airline_Name
                                }
                              </div>
                            </div>
                            <div className="download">
                              {ticketLoadingIndexes?.includes(tripIndex) ? (
                                <div
                                  className="spinner-border text-primary ticket-pop-loading"
                                  role="status"
                                />
                              ) : (
                                <HiDownload
                                  style={{ fontSize: 22 }}
                                  onClick={() => {
                                    !ticketLoadingIndexes?.includes(
                                      tripIndex
                                    ) &&
                                      handleDownloadPdf(
                                        BookingDetails?.portal_booking_no,
                                        tripInfo?.Airline_PNR,
                                        tripIndex
                                      );
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      }
                    )}
                  <div className="download-all">
                    <button
                      className="download-btn"
                      onClick={async () => {
                        if (!BookingDetails || !BookingDetails.AirPNRDetails)
                          return;

                        try {
                          await Promise.all(
                            BookingDetails.AirPNRDetails.map(
                              (tripInfo: any, tripIndex: number) =>
                                handleDownloadPdf(
                                  BookingDetails?.portal_booking_no,
                                  tripInfo?.Airline_PNR,
                                  tripIndex
                                ).catch((err) => {
                                  console.error(
                                    `Download failed for trip ${tripIndex}`,
                                    err
                                  );
                                })
                            )
                          );
                        } catch (err) {
                          console.error("Error downloading all tickets", err);
                        }
                      }}
                    >
                      Download All Tickets
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal fade"
            id={`downloadinvoice_popup`}
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex={-1}
            aria-labelledby="downloadticket-popup"
            aria-hidden="true"
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: 500 }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title fs-5" id="edit-profile">
                    Download Invoice(s)
                  </h4>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body mt-2">
                  {BookingDetails &&
                    BookingDetails?.AirPNRDetails?.map(
                      (tripInfo: any, tripIndex: any) => {
                        return (
                          <div className="invoice-card">
                            <div className="icon invoice">
                              <BsFillFileTextFill />
                            </div>
                            <div className="info">
                              <div className="title">
                                {tripInfo?.Flights[0]?.Segments[0]?.Origin} →{" "}
                                {
                                  tripInfo?.Flights[0]?.Segments[
                                    tripInfo?.Flights[0]?.Segments?.length - 1
                                  ]?.Destination
                                }
                              </div>
                              <div className="date">
                                {moment(
                                  tripInfo?.Flights[0]?.Segments[0]
                                    ?.Departure_DateTime,
                                  "MM/DD/YYYY HH:mm:ss"
                                ).format("D MMMM")}
                                ,{" "}
                                {
                                  tripInfo?.Flights[0]?.Segments[0]
                                    ?.Airline_Name
                                }
                              </div>
                            </div>
                            <div className="download">
                              {invoiceLoadingIndexes?.includes(tripIndex) ? (
                                <div
                                  className="spinner-border text-primary ticket-pop-loading"
                                  role="status"
                                />
                              ) : (
                                <HiDownload
                                  style={{ fontSize: 22 }}
                                  onClick={() => {
                                    !invoiceLoadingIndexes?.includes(
                                      tripIndex
                                    ) &&
                                      handleDownloadInvoice(
                                        BookingDetails?.portal_booking_no,
                                        tripInfo?.Airline_PNR,
                                        tripIndex
                                      );
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      }
                    )}
                  <div className="download-all">
                    <button
                      className="download-btn"
                      onClick={async () => {
                        if (!BookingDetails || !BookingDetails.AirPNRDetails)
                          return;

                        try {
                          await Promise.all(
                            BookingDetails.AirPNRDetails.map(
                              (tripInfo: any, tripIndex: number) =>
                                handleDownloadInvoice(
                                  BookingDetails?.portal_booking_no,
                                  tripInfo?.Airline_PNR,
                                  tripIndex
                                ).catch((err) => {
                                  console.error(
                                    `Download failed for trip ${tripIndex}`,
                                    err
                                  );
                                })
                            )
                          );
                        } catch (err) {
                          console.error("Error downloading all Invoices", err);
                        }
                      }}
                    >
                      Download All Invoices
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <SendTicketDialog
            portal_booking_no={BookingDetails?.portal_booking_no}
          />
          <SendInvoiceDialog
            portal_booking_no={BookingDetails?.portal_booking_no}
          />
        </div>
      )}
    </>
  );
};

export default TripConfirmation;
